/**
 * ThreadAddPeoplePanel — flag-gated modal to add workspace members to the active
 * thread (group-threading P2c). Purely presentational: the parent
 * (`useThreadAddPeople`) owns the model, the busy/notice state, and the authorized
 * P2a write. This component never queries, never writes, and never authorizes — it
 * renders the roster and forwards clicks.
 *
 * Inert by contract: renders `null` unless `enabled && isOpen && model`, and the
 * controller's `enabled` is the account-scoped flag (default OFF).
 */
import React from 'react';
import ReactDOM from 'react-dom';
import type { AddPeopleCandidate, AddPeopleModel } from '../../services/threadAddPeople';
import { disambiguateParticipantLabel } from '../../services/groupThreads/senderIdentity';
import type { ThreadPostRole } from '../../repositories/threadMembershipWriteClient';

export const ADD_PEOPLE_COPY = {
  title: 'Add people',
  assistantName: 'Prest0n',
  assistantAlwaysHere: 'In every thread — replies per the @ mention setting',
  loading: 'Loading workspace members…',
  emptyNoRoster: "You're the only person in this workspace so far.",
  emptyNoRosterHint: 'Invite a teammate and they can join your threads as soon as they accept.',
  emptyAllIn: 'Everyone in this workspace is already in this thread.',
  alreadyHere: 'Already in this thread',
  notYetAvailable: 'Not available yet',
  invited: 'Invited — waiting for them to accept',
  invitedHint: "They'll appear here as soon as they accept. You can resend or cancel in Team & Roles.",
  inviteExpired: 'Invitation expired — resend it in Team & Roles',
  invitePending: "Hasn't accepted yet",
  manageTeam: 'Invite a teammate',
  retry: 'Try again',
  add: 'Add',
  adding: 'Adding…',
  close: 'Close',
  roleContributor: 'Contributor',
  roleViewer: 'Read-only',
  roleHintContributor: 'Can read and post in this thread.',
  roleHintViewer: 'Can read this thread but not post.',
} as const;

/** Where "Invite a teammate" sends the admin. Kept here so the copy and the destination move together. */
export const TEAM_SETTINGS_PATH = '/settings/team';

export interface ThreadAddPeoplePanelProps {
  /** Account-scoped enablement (from the controller). Absent / false ⇒ renders nothing. */
  enabled?: boolean;
  isOpen: boolean;
  loading?: boolean;
  model: AddPeopleModel | null | undefined;
  busyUserId?: string | null;
  notice?: string | null;
  /**
   * The roster READ failed. Renders a retry instead of an empty state — a
   * workspace that couldn't be loaded is not a workspace with nobody in it.
   */
  loadError?: string | null;
  onRetry?: () => void;
  /** Opens Team & Roles so an admin can invite someone without leaving a dead end. */
  onInvite?: () => void;
  /** #21: the per-thread role newly-added people receive; segmented toggle. */
  addRole?: ThreadPostRole;
  onRoleChange?: (role: ThreadPostRole) => void;
  onAdd?: (userId: string) => void;
  onClose?: () => void;
  'data-testid'?: string;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.32)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2147483000,
};

const cardStyle: React.CSSProperties = {
  background: 'var(--surface, #fff)',
  color: 'var(--text, #16181d)',
  width: 'min(420px, calc(100vw - 32px))',
  maxHeight: 'min(72vh, 560px)',
  borderRadius: 14,
  boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 16px',
  borderBottom: '1px solid rgba(127,127,127,0.18)',
  fontWeight: 600,
};

const listStyle: React.CSSProperties = { overflowY: 'auto', padding: '8px 0', flex: 1 };

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 16px',
};

const labelWrapStyle: React.CSSProperties = { minWidth: 0, display: 'flex', flexDirection: 'column' };
const labelStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 14,
};
const secondaryStyle: React.CSSProperties = { fontSize: 12, opacity: 0.62 };

const addBtnStyle: React.CSSProperties = {
  flex: '0 0 auto',
  border: '1px solid rgba(127,127,127,0.35)',
  background: 'transparent',
  color: 'inherit',
  borderRadius: 8,
  padding: '4px 12px',
  fontSize: 13,
  cursor: 'pointer',
};

const noticeStyle: React.CSSProperties = {
  margin: '10px 16px 0',
  padding: '8px 10px',
  borderRadius: 8,
  background: 'rgba(220,72,72,0.12)',
  color: '#c0392b',
  fontSize: 13,
};

const sectionLabelStyle: React.CSSProperties = {
  padding: '10px 16px 4px',
  fontSize: 11,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  opacity: 0.55,
};

const emptyStyle: React.CSSProperties = { padding: '18px 16px', fontSize: 14, opacity: 0.72 };

const emptyHintStyle: React.CSSProperties = {
  padding: '0 16px 4px',
  fontSize: 13,
  opacity: 0.6,
  lineHeight: 1.4,
};

const linkBtnStyle: React.CSSProperties = {
  margin: '8px 16px 4px',
  border: '1px solid rgba(198,167,94,0.55)',
  background: 'rgba(198,167,94,0.14)',
  color: '#8a6d2f',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

/** Muted, non-actionable row: someone who exists but can't be added right now. */
const InfoRow: React.FC<{ label: string; secondary?: string; note?: string; testAttr?: Record<string, string> }> = ({
  label,
  secondary,
  note,
  testAttr,
}) => (
  <div style={{ ...rowStyle, opacity: 0.6 }} {...(testAttr ?? {})}>
    <span style={labelWrapStyle}>
      <span style={labelStyle} title={label}>
        {label}
      </span>
      {secondary && <span style={secondaryStyle}>{secondary}</span>}
      {note && <span style={secondaryStyle}>{note}</span>}
    </span>
  </div>
);

const CandidateRow: React.FC<{
  candidate: AddPeopleCandidate;
  busy: boolean;
  anyBusy: boolean;
  onAdd?: (userId: string) => void;
}> = ({ candidate, busy, anyBusy, onAdd }) => {
  /**
   * A HUMAN account whose display name collides with the assistant (the
   * production case: a real teammate account on prest0n@prest0.ai) must not
   * render as "Prest0n" next to an Add button — the owner read exactly that
   * row as the assistant being addable (2026-08-07). Same W2 rule the
   * transcript chips use: never present a human under the assistant's name.
   */
  const label = disambiguateParticipantLabel(
    candidate.label,
    { email: candidate.secondary },
    candidate.userId,
  );
  return (
  <div style={rowStyle} data-add-people-row={candidate.userId}>
    <span style={labelWrapStyle}>
      <span style={labelStyle} title={label}>
        {label}
      </span>
      {candidate.secondary && <span style={secondaryStyle}>{candidate.secondary}</span>}
    </span>
    <button
      type="button"
      style={{ ...addBtnStyle, opacity: anyBusy && !busy ? 0.5 : 1 }}
      disabled={anyBusy}
      data-add-people-add={candidate.userId}
      onClick={() => onAdd?.(candidate.userId)}
    >
      {busy ? ADD_PEOPLE_COPY.adding : ADD_PEOPLE_COPY.add}
    </button>
  </div>
  );
};

export const ThreadAddPeoplePanel: React.FC<ThreadAddPeoplePanelProps> = ({
  enabled,
  isOpen,
  loading,
  model,
  busyUserId,
  notice,
  loadError,
  onRetry,
  onInvite,
  addRole,
  onRoleChange,
  onAdd,
  onClose,
  ...rest
}) => {
  if (!enabled || !isOpen) return null;

  const anyBusy = !!busyUserId;
  const addable = model?.addable ?? [];
  const existing = model?.existing ?? [];
  const unavailable = model?.unavailable ?? [];
  const pending = model?.pending ?? [];
  const failed = !!loadError;

  // Four distinct outcomes, four distinct answers. Collapsing the first into the
  // others is what produced "No one else in this workspace to add." on a
  // workspace that demonstrably had someone else in it.
  const showEmpty = !loading && !failed && addable.length === 0;
  const emptyMsg =
    existing.length > 0
      ? ADD_PEOPLE_COPY.emptyAllIn
      : unavailable.length > 0 || pending.length > 0
        ? ''
        : ADD_PEOPLE_COPY.emptyNoRoster;
  // The dead end this dialog used to be: nobody to add, and nothing to do about
  // it. Offer the invite path whenever there is genuinely no one else yet.
  const showInvite =
    showEmpty && existing.length === 0 && unavailable.length === 0 && pending.length === 0 && !!onInvite;
  const role: ThreadPostRole = addRole === 'viewer' ? 'viewer' : 'contributor';
  const roleTabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '6px 10px',
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: active ? '#8a6d2f' : 'inherit',
    background: active ? 'rgba(198,167,94,0.16)' : 'transparent',
    border: 'none',
    borderRadius: 7,
    cursor: anyBusy ? 'default' : 'pointer',
  });

  const content = (
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label={ADD_PEOPLE_COPY.title}
      // Marks this as a body-portaled chat surface so the chat's document-level
      // click-outside handlers don't treat interactions here as "outside the chat"
      // (which would close the whole pane). Mirrors ConfirmationModal.
      data-prest0n-portal="true"
      data-testid={rest['data-testid']}
      onClick={onClose}
    >
      <div style={cardStyle} data-add-people-panel="" onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span>{ADD_PEOPLE_COPY.title}</span>
          <button
            type="button"
            style={{ ...addBtnStyle, border: 'none' }}
            aria-label={ADD_PEOPLE_COPY.close}
            data-add-people-close=""
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {notice && (
          <div style={noticeStyle} role="alert" data-add-people-notice="">
            {notice}
          </div>
        )}

        {/* #21: per-thread access for the next person added. Thread-scoped and
            independent of workspace roles. Hidden while the roster is unreadable —
            picking a role for a list we can't show is noise. */}
        {!failed && (
        <div style={{ padding: '10px 16px 2px' }} data-add-people-role={role}>
          <div
            style={{
              display: 'flex',
              gap: 4,
              background: 'rgba(127,127,127,0.10)',
              borderRadius: 9,
              padding: 3,
            }}
            role="tablist"
            aria-label="Access for added people"
          >
            <button
              type="button"
              role="tab"
              aria-selected={role === 'contributor'}
              disabled={anyBusy}
              style={roleTabStyle(role === 'contributor')}
              data-add-people-role-contributor=""
              onClick={() => onRoleChange?.('contributor')}
            >
              {ADD_PEOPLE_COPY.roleContributor}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={role === 'viewer'}
              disabled={anyBusy}
              style={roleTabStyle(role === 'viewer')}
              data-add-people-role-viewer=""
              onClick={() => onRoleChange?.('viewer')}
            >
              {ADD_PEOPLE_COPY.roleViewer}
            </button>
          </div>
          <div style={{ ...secondaryStyle, padding: '5px 2px 0' }}>
            {role === 'viewer' ? ADD_PEOPLE_COPY.roleHintViewer : ADD_PEOPLE_COPY.roleHintContributor}
          </div>
        </div>
        )}

        <div style={listStyle}>
          {/*
            Prest0n is a member of EVERY thread by construction (owner directive
            2026-08-07) — it must never appear as an addable candidate, and the
            user can never remove it. A pinned, buttonless row states that in
            both role tabs (the tabs pick the role for the NEXT human added; the
            assistant's participation is not role-scoped and not configurable —
            whether it REPLIES is the thread's @ mention setting, which the row
            points at). Rendered above loading/failure states because it is
            static truth, not fetched data.
          */}
          {!failed && (
            <div style={rowStyle} data-add-people-assistant="">
              <span style={labelWrapStyle}>
                <span style={labelStyle}>{ADD_PEOPLE_COPY.assistantName}</span>
                <span style={secondaryStyle}>{ADD_PEOPLE_COPY.assistantAlwaysHere}</span>
              </span>
            </div>
          )}
          {/* Instant-open (2026-08-09): the loading line shows only when there
              is genuinely nothing to render yet. A reopen (state kept) or a
              seeded roster paints immediately; load() is silent background
              revalidation that must never blank rows the user can see. */}
          {loading && addable.length === 0 && existing.length === 0 && (
            <div style={emptyStyle}>{ADD_PEOPLE_COPY.loading}</div>
          )}

          {/* A read that FAILED. Never an empty state — the workspace may well be
              full of people we simply couldn't fetch. */}
          {!loading && failed && (
            <div data-add-people-load-error="">
              <div style={emptyStyle}>{loadError}</div>
              {onRetry && (
                <button type="button" style={linkBtnStyle} data-add-people-retry="" onClick={onRetry}>
                  {ADD_PEOPLE_COPY.retry}
                </button>
              )}
            </div>
          )}

          {!failed &&
            addable.map((c) => (
              <CandidateRow
                key={c.userId}
                candidate={c}
                busy={busyUserId === c.userId}
                anyBusy={anyBusy}
                onAdd={onAdd}
              />
            ))}

          {showEmpty && emptyMsg && (
            <div style={emptyStyle} data-add-people-empty="">
              {emptyMsg}
            </div>
          )}

          {showInvite && (
            <>
              <div style={emptyHintStyle}>{ADD_PEOPLE_COPY.emptyNoRosterHint}</div>
              <button type="button" style={linkBtnStyle} data-add-people-invite="" onClick={onInvite}>
                {ADD_PEOPLE_COPY.manageTeam}
              </button>
            </>
          )}

          {!failed && existing.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{ADD_PEOPLE_COPY.alreadyHere}</div>
              {existing.map((c) => (
                <InfoRow
                  key={c.userId}
                  label={disambiguateParticipantLabel(c.label, { email: c.secondary }, c.userId)}
                  secondary={c.secondary}
                  testAttr={{ 'data-add-people-existing': c.userId }}
                />
              ))}
            </>
          )}

          {/* Members the workspace exists but won't admit yet — shown WITH the
              reason instead of being silently filtered out of the picker. */}
          {!failed && unavailable.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{ADD_PEOPLE_COPY.notYetAvailable}</div>
              {unavailable.map((c) => (
                <InfoRow
                  key={c.userId}
                  label={c.label}
                  secondary={c.secondary}
                  note={c.blockedReason}
                  testAttr={{ 'data-add-people-unavailable': c.userId }}
                />
              ))}
            </>
          )}

          {/* "I invited them — where are they?" answered in place, so nobody has
              to guess whether the invite worked. */}
          {!failed && pending.length > 0 && (
            <>
              <div style={sectionLabelStyle}>{ADD_PEOPLE_COPY.invited}</div>
              {pending.map((p) => (
                <InfoRow
                  key={p.inviteId}
                  label={p.displayName || p.email}
                  secondary={p.displayName ? p.email : undefined}
                  note={
                    p.status === 'expired'
                      ? ADD_PEOPLE_COPY.inviteExpired
                      : ADD_PEOPLE_COPY.invitePending
                  }
                  testAttr={{ 'data-add-people-pending': p.inviteId }}
                />
              ))}
              <div style={emptyHintStyle}>{ADD_PEOPLE_COPY.invitedHint}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Render into document.body so the fixed overlay escapes the docked chat
  // widget's transformed stacking context (which was trapping position:fixed and
  // letting thread names / chips bleed over the modal). SSR/test-safe: fall back to
  // inline render when there is no document (node unit env uses renderToStaticMarkup).
  return typeof document !== 'undefined' && document.body
    ? ReactDOM.createPortal(content, document.body)
    : content;
};

export default ThreadAddPeoplePanel;
